import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Loader, CheckCircle, XCircle, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../utils/toast';
import { GOOGLE_API_CONFIG } from '../config/google';
import { generateOAuthState } from '../utils/googleAuth';
import SEO from '../components/SEO';

interface CalendarEvent {
  id: string;
  title: string;
  start_datetime: string;
  end_datetime: string;
  sync_status: 'pending_google_sync' | 'synced' | 'sync_failed';
}

interface PendingCalendar {
  id: string;
  name: string;
  description: string;
  setup_status: string;
  created_by_agent: boolean;
  agent_metadata: Record<string, any>;
  user_id: string;
  profiles?: {
    email: string;
  };
}

export default function SetupCalendar() {
  const { setupToken } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [calendar, setCalendar] = useState<PendingCalendar | null>(null);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ synced: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate(`/login?returnTo=/setup-calendar/${setupToken}`, { replace: true });
      return;
    }

    if (setupToken) {
      loadPendingCalendar();
    }
  }, [user, authLoading, setupToken, navigate]);

  const loadPendingCalendar = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: calendarData, error: calendarError } = await supabase
        .from('calendars')
        .select('id, name, description, setup_status, created_by_agent, agent_metadata, user_id, profiles!calendars_user_id_fkey(email)')
        .eq('setup_token', setupToken)
        .maybeSingle();

      if (calendarError) throw calendarError;

      if (!calendarData) {
        setError('Invalid or expired setup link');
        return;
      }

      if (calendarData.user_id !== user?.id) {
        setError('This calendar belongs to a different account. Please log in with the correct account to complete setup.');
        return;
      }

      if (calendarData.setup_status === 'active') {
        toast.success('Calendar already set up');
        navigate(`/calendar/${calendarData.id}`);
        return;
      }

      setCalendar(calendarData);

      const { data: eventsData, error: eventsError } = await supabase
        .from('calendar_events')
        .select('id, title, start_datetime, end_datetime, sync_status')
        .eq('calendar_id', calendarData.id)
        .order('start_datetime', { ascending: true });

      if (eventsError) throw eventsError;

      setEvents(eventsData || []);
    } catch (err) {
      console.error('Error loading pending calendar:', err);
      setError('Failed to load calendar setup information');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGoogleCalendar = async () => {
    try {
      setError(null);

      sessionStorage.setItem('calendar_return_to', `/setup-calendar/${setupToken}`);
      sessionStorage.setItem('pending_setup_calendar_id', calendar?.id || '');
      sessionStorage.setItem('setup_pending_oauth', 'true');

      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      const REDIRECT_URI = `${window.location.origin}/google-callback.html`;

      authUrl.searchParams.append('client_id', GOOGLE_API_CONFIG.CLIENT_ID);
      authUrl.searchParams.append('redirect_uri', REDIRECT_URI);
      authUrl.searchParams.append('response_type', 'token');
      authUrl.searchParams.append('scope', 'https://www.googleapis.com/auth/calendar');
      authUrl.searchParams.append('prompt', 'consent');
      authUrl.searchParams.append('state', generateOAuthState());

      window.location.href = authUrl.toString();
    } catch (err) {
      console.error('Error initiating Google OAuth:', err);
      setError('Failed to connect to Google Calendar');
    }
  };

  const processGoogleAuth = async (accessToken: string) => {
    if (!calendar) return;

    try {
      setSyncing(true);

      const calendarName = calendar.name;
      const createResponse = await fetch(
        'https://www.googleapis.com/calendar/v3/calendars',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            summary: calendarName,
            timeZone: 'UTC',
          }),
        }
      );

      if (!createResponse.ok) {
        throw new Error('Failed to create Google Calendar');
      }

      const createdCalendar = await createResponse.json();
      const googleCalendarId = createdCalendar.id;

      const aclResponse = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
          googleCalendarId
        )}/acl`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            role: 'reader',
            scope: {
              type: 'default',
            },
          }),
        }
      );

      if (!aclResponse.ok) {
        console.warn('Failed to make calendar public');
      }

      const googleCalendarUrl = `https://calendar.google.com/calendar/ical/${googleCalendarId}/public/basic.ics`;

      await supabase
        .from('calendars')
        .update({
          onboarding_status: 'google_connected',
          google_connected_at: new Date().toISOString(),
        })
        .eq('id', calendar.id);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const syncResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-events-to-google`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            calendar_id: calendar.id,
            google_access_token: accessToken,
            google_calendar_id: googleCalendarId,
          }),
        }
      );

      if (!syncResponse.ok) {
        throw new Error('Failed to sync events to Google Calendar');
      }

      const syncResult = await syncResponse.json();

      setSyncProgress({
        synced: syncResult.synced_count,
        total: syncResult.total_events,
      });

      const COMMUNITY_CATEGORY_ID = 'e0e3a26e-fd0e-4d43-b930-2b1c8c5fd1b3';

      const { error: updateError } = await supabase
        .from('calendars')
        .update({
          setup_status: 'active',
          setup_token: null,
          google_calendar_url: googleCalendarUrl,
          google_calendar_id: googleCalendarId,
          category_id: COMMUNITY_CATEGORY_ID,
        })
        .eq('id', calendar.id);

      if (updateError) throw updateError;

      if (calendar.agent_metadata?.agent_identifier) {
        try {
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          if (currentSession) {
            await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-agent-setup-complete`,
              {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${currentSession.access_token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  calendar_id: calendar.id,
                  agent_identifier: calendar.agent_metadata.agent_identifier,
                }),
              }
            );
          }
        } catch (webhookError) {
        }
      }

      localStorage.removeItem('google_token');
      sessionStorage.removeItem('pending_setup_calendar_id');

      toast.success(
        `Calendar setup complete! ${syncResult.synced_count} events synced.`
      );
      navigate(`/calendar/${calendar.id}`);
    } catch (err) {
      console.error('Error during setup:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to complete calendar setup'
      );
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (!calendar) return;

    const pendingCalendarId = sessionStorage.getItem('pending_setup_calendar_id');
    const googleToken = localStorage.getItem('google_token');

    if (pendingCalendarId === calendar.id && googleToken) {
      processGoogleAuth(googleToken);
    }
  }, [calendar]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading setup information...</p>
        </div>
      </div>
    );
  }

  if (error || !calendar) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center p-4">
        <SEO
          title="Calendar Setup Error"
          description="Calendar setup error"
        />
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <XCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Setup Error</h2>
          <p className="text-gray-600 mb-6">{error || 'Invalid setup link'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center p-4">
      <SEO
        title="Complete Calendar Setup"
        description="Complete your calendar setup by connecting to Google Calendar"
      />
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full">
        <div className="text-center mb-8">
          <Calendar className="h-16 w-16 text-blue-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Complete Your Calendar Setup
          </h1>
          <p className="text-gray-600">
            Your calendar is ready to be activated
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {calendar.name}
          </h2>
          {calendar.description && (
            <p className="text-gray-600 mb-4">{calendar.description}</p>
          )}
          <div className="flex items-center text-sm text-gray-700">
            <Clock className="h-4 w-4 mr-2" />
            <span>{events.length} events ready to sync</span>
          </div>
        </div>

        {events.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Event Preview</h3>
            <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
              {events.slice(0, 5).map((event) => (
                <div
                  key={event.id}
                  className="flex items-start py-2 border-b border-gray-200 last:border-0"
                >
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {event.title}
                    </p>
                    <p className="text-xs text-gray-600">
                      {new Date(event.start_datetime).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
              {events.length > 5 && (
                <p className="text-xs text-gray-600 text-center pt-2">
                  +{events.length - 5} more events
                </p>
              )}
            </div>
          </div>
        )}

        {syncing ? (
          <div className="text-center py-8">
            <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-700 font-medium mb-2">
              Setting up your calendar...
            </p>
            {syncProgress.total > 0 && (
              <p className="text-sm text-gray-600">
                Synced {syncProgress.synced} of {syncProgress.total} events
              </p>
            )}
          </div>
        ) : (
          <button
            onClick={handleConnectGoogleCalendar}
            disabled={syncing}
            className="w-full py-3 px-6 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors flex items-center justify-center disabled:opacity-50"
          >
            <Calendar className="h-5 w-5 mr-2" />
            Connect Google Calendar & Sync Events
          </button>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
